import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import {
  IRentAdvancePayment,
  IRentPayment,
} from 'src/@types/Choqorak/MainTables/RentPayment.ts/RentPayment';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import { GetMonthName } from 'src/sections/common/GetMonthName';
import Label from 'src/components/Label';
import CalculateDuration from 'src/sections/common/CalculateDuration';
import CalculateMonthsOnly from 'src/sections/common/CalculateMonthsOnly';

// ----------------------------------------------------------------------
interface Props {
  id: number;
}
export default observer(function RentAdvancePayment({ id }: Props) {
  const { RentPaymentStore, ContractInfoStore } = useStore();
  const { translate } = useLocales();
  const {
    createRentPayment,
    updateRentPayment,
    editMode,
    selectedRentPayment,
    clearSelectedRentPayment,
    setOpenCloseCreateDialog,
  } = RentPaymentStore;
  const { selectedContractInfoDetail } = ContractInfoStore;
  const [durationOfDate, setDurationOfDate] = useState<{ year: number; month: number }[]>([]);

  const { setOpenCloseRentAdvancePaymentDialog } = ContractInfoStore;

  const navigate = useNavigate();
  const [paid, setPaid] = useState<number | undefined>(undefined);
  const [years, setYear] = useState<string>(''); // State for the year
  const [months, setMonth] = useState<string>(''); // State for the month
  const { enqueueSnackbar } = useSnackbar();

  const NewRentPaymentSchema = Yup.object().shape({
    paidAmount: Yup.string().required(`${translate('Validation.paidAmount')}`),
    description: Yup.string().required(`${translate('Validation.description')}`),
  });

  const defaultValues = useMemo<IRentAdvancePayment>(
    () => ({
      contractId: selectedContractInfoDetail?.contractId || undefined,
      year: new Date(),
      month: new Date(),
      startDate: new Date(),
      endDate: new Date(),

      durationOfAdvanceRents: [{ year: 0, month: 0 }], // Initialize with one object in the array
    }),
    [selectedContractInfoDetail]
  );

  const methods = useForm<IRentAdvancePayment>({
    resolver: yupResolver(NewRentPaymentSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = async (data: IRentAdvancePayment) => {
    const newData = {
      ...data,
      durationOfAdvanceRents: durationOfDate,
      paymentDate: new Date(data.paymentDate!),
    };

    try {
      await createRentPayment(newData);
      setOpenCloseRentAdvancePaymentDialog();
      reset();
      enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
    } catch (err: any) {
      try {
        const json = JSON.parse(err?.request?.response);

        if (json?.error) {
          // Loop through validation errors
          Object.entries(json.error).forEach(([field, message]) => {
            setError('afterSubmit', { type: 'manual', message: message as string });
            // optionally: enqueueSnackbar(message as string, { variant: 'error' });
          });
        } else {
          setError('afterSubmit', { type: 'manual', message: json?.message || 'Unexpected error' });
        }
      } catch {
        enqueueSnackbar('Unexpected error occurred', { variant: 'error' });
      }
    }
  };

  const FromYearsDate = watch('startDate'); // Watch the 'FromYear' field
  const ToYearsDate = watch('endDate'); // Watch the 'ToYear' field

  const convertToPersianDate = (gregorianDate: Date) => {
    const persianYear = gregorianDate.toLocaleString('fa-IR-u-ca-persian', {
      year: 'numeric',
    });
    const persianMonth = gregorianDate.toLocaleString('fa-IR-u-ca-persian', {
      month: 'numeric',
    });

    const westernYear = persianYear.replace(/[۰-۹]/g, (digit) =>
      '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
    );
    const westernMonth = persianMonth.replace(/[۰-۹]/g, (digit) =>
      '۰۱۲۳۴۵۶۷۸۹'.indexOf(digit).toString()
    );

    return { year: parseInt(westernYear), month: parseInt(westernMonth) };
  };

  const calculatePersianDuration = (fromDate: Date, toDate: Date) => {
    const fromPersian = convertToPersianDate(fromDate);
    const toPersian = convertToPersianDate(toDate);

    const duration = [];
    let currentYear = fromPersian.year;
    let currentMonth = fromPersian.month;

    while (currentYear < toPersian.year || (currentYear === toPersian.year && currentMonth <= toPersian.month)) {
      duration.push({ year: currentYear, month: currentMonth });

      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    return duration;
  };
  const toUTCDateOnly = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Date(Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate()));
  };
  useEffect(() => {
    if (FromYearsDate && ToYearsDate) {
      const fromDate = toUTCDateOnly(FromYearsDate);
      const toDate = toUTCDateOnly(ToYearsDate);

      if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
        const persianDuration = calculatePersianDuration(fromDate, toDate);
        setDurationOfDate(persianDuration);
      } else {
        console.error('Invalid date format');
      }
    }
  }, [FromYearsDate, ToYearsDate]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* First column for the form */}
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Label sx={{ p: 3, fontSize: 18, color: 'white', mb: 2 }} color="warning">
              {translate('RentPayment.Month')}:
              {CalculateMonthsOnly(new Date(FromYearsDate!), new Date(ToYearsDate!))}
            </Label>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <LocalizDatePicker
                name="startDate"
                // label={translate('Employee.date')}
                label={translate('Report.FromDate')}
                control={control}
                showAsterisk={true}
                views={['year', 'month']}
              />
              <LocalizDatePicker
                name="endDate"
                // label={translate('Employee.date')}
                label={translate('Report.ToDate')}
                control={control}
                showAsterisk={true}
                views={['year', 'month']}
              />
              <RHFTextField
                name="paidAmount"
                label={translate('RentPayment.PaidAmount')}
                type="number"
                value={paid}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : Number(e.target.value);
                  setValue('paidAmount', value);
                }}
                autoFocus
              />
              <LocalizDatePicker
                name="paymentDate"
                label={translate('RentPayment.PaymentDate')}
                control={control}
                showAsterisk={true}
              />
              <RHFTextField
                name="serialNumber"
                label={translate('RentPaymentPrint.SerialNumber')}
                showAsterisk={true}
              />
              <RHFTextField
                name="description"
                label={translate('Report.Description')}
                showAsterisk={true}
              />

              <Stack spacing={2} sx={{ mb: 2 }}>
                <>
                  {/* Hidden File Input */}
                  <input
                    type="file"
                    // accept=".jpg"
                    id="contractattachment"
                    onChange={(e) => {
                      const file = e.target.files ? e.target.files[0] : null;
                      setValue('attachment', file);
                    }}
                    style={{ display: 'none' }}
                  />

                  {/* TextField with Attachment Icon */}
                  <TextField
                    label={translate('contractsInfo.Attachments')}
                    value={watch('attachment')?.name || ''} // Display file name
                    placeholder="No file chosen"
                    InputProps={{
                      readOnly: true, // Prevent manual input
                      startAdornment: (
                        <InputAdornment position="start">
                          {/* Attachment Icon */}
                          <label htmlFor="contractattachment">
                            <IconButton component="span">
                              <AttachFileIcon />
                            </IconButton>
                          </label>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    size="medium"
                    sx={{ m: 1, width: '100%' }}
                  />
                </>
              </Stack>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  setOpenCloseRentAdvancePaymentDialog();
                  clearSelectedRentPayment();
                }}
              >
                {translate('CRUD.Close')}
              </Button>
            </Stack>
          </Card>
        </Grid>

        {/* Second column for displaying year and month information */}
      </Grid>
    </FormProvider>
  );
});
